import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createEvent } from '../../store/slices/eventSlice';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
  Calendar,
  MapPin,
  DollarSign,
  Users,
  Image as ImageIcon,
  ChevronLeft,
  XCircle,
  Plus,
  Send,
  Loader2,
  Map,
  Globe,
  Building2,
  Info,
  Calculator,
  Check,
  Copy,
  Trash2,
  Ticket,
  Clock,
  AlertTriangle
} from 'lucide-react';

const InputGroup = ({ label, icon, children }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1 flex items-center gap-2">
      {icon}
      {label}
    </label>
    {children}
  </div>
);

const MIN_EVENT_TICKETS = 10;

const normalizeTicketInventory = (ticketTypes) => {
  const normalized = ticketTypes.map((ticket) => ({
    ...ticket,
    totalQuantity: Math.max(0, Number(ticket.totalQuantity) || 0),
    ticketsIncluded: Math.max(1, Number(ticket.ticketsIncluded) || 1),
  }));

  const total = normalized.reduce((sum, ticket) => sum + ticket.totalQuantity, 0);
  if (normalized.length > 0 && total < MIN_EVENT_TICKETS) {
    normalized[0].totalQuantity += MIN_EVENT_TICKETS - total;
  }

  return normalized;
};

const normalizeEventDates = (date, additionalDates = []) => (
  [...new Set([date, ...additionalDates].filter(Boolean))].sort()
);

const getSalonLocationDefaults = (salon) => ({
  address: salon?.address || '',
  city: salon?.city || '',
  province: salon?.province || '',
  country: salon?.country || 'Argentina',
});

const normalizeNonNegativeInput = (value) => {
  if (value === '') return '';
  return String(Math.max(0, Number(value) || 0));
};

const CreateEvent = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [additionalDate, setAdditionalDate] = useState('');
  const [additionalDates, setAdditionalDates] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [salesMode, setSalesMode] = useState('tickets');
  const [bookingAmount, setBookingAmount] = useState('');
  const [bookingPaidAmount, setBookingPaidAmount] = useState('');
  const [bookingPaymentStatus, setBookingPaymentStatus] = useState('pending');
  const [bookingNotes, setBookingNotes] = useState('');
  // const [price, setPrice] = useState(''); // Legacy: se calcula solo
  // const [totalTickets, setTotalTickets] = useState(''); // Legacy: se calcula solo

  // Nuevo estado para múltiples tickets
  const [ticketTypes, setTicketTypes] = useState([
    { id: Date.now(), name: '', price: '', totalQuantity: MIN_EVENT_TICKETS, ticketsIncluded: 1, description: '' }
  ]);

  const [expenses, setExpenses] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  // Dirección estructurada
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [country, setCountry] = useState('Argentina');

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [createdEvent, setCreatedEvent] = useState(null);
  const [createdBatchCount, setCreatedBatchCount] = useState(1);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false); // Nuevo estado para el modal de éxito
  const [salon, setSalon] = useState(null);
  const [loadingSalon, setLoadingSalon] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();

  React.useEffect(() => {
    fetchSalon();
  }, [slug]);

  const fetchSalon = async () => {
    try {
      const { data } = await axios.get(`/api/clients/${slug}`);
      setSalon(data);
    } catch (err) {
      console.error('Error fetching salon:', err);
    } finally {
      setLoadingSalon(false);
    }
  };

  const isMpConnected = !!salon?.hasMpConnected;
  const isBookingMode = salesMode === 'booking';
  // MP solo es obligatorio para productoras (comisión). Salones suscriptos pueden crear eventos sin MP.
  const isMpRequired = !isBookingMode && salon?.billingModel !== 'subscription' && !isMpConnected;
  const isSubscriptionClient = salon?.billingModel === 'subscription';
  const eventDates = React.useMemo(() => normalizeEventDates(date, additionalDates), [date, additionalDates]);
  const eventDatesKey = eventDates.join('|');
  const salonHasLocation = Boolean(salon?.address || salon?.city || salon?.province);

  const applySalonLocation = () => {
    const defaults = getSalonLocationDefaults(salon);
    setAddress(defaults.address);
    setCity(defaults.city);
    setProvince(defaults.province);
    setCountry(defaults.country);
  };

  React.useEffect(() => {
    if (isSubscriptionClient) {
      setIsPublic(false);
    }
  }, [isSubscriptionClient]);

  React.useEffect(() => {
    if (!salon) return;
    const defaults = getSalonLocationDefaults(salon);
    setAddress((current) => current || defaults.address);
    setCity((current) => current || defaults.city);
    setProvince((current) => current || defaults.province);
    setCountry((current) => current || defaults.country);
  }, [salon]);

  React.useEffect(() => {
    setAdditionalDates((current) => current.filter((item) => item && item !== date));
  }, [date]);

  React.useEffect(() => {
    if (allDay) {
      setStartTime('');
      setEndTime('');
    }
  }, [allDay]);

  React.useEffect(() => {
    if (!eventDates.length || (!allDay && (!startTime || !endTime))) {
      setAvailability(null);
      return;
    }

    let alive = true;
    const timer = window.setTimeout(async () => {
      setCheckingAvailability(true);
      try {
        const { data } = await axios.get(`/${slug}/api/events/availability`, {
          params: { date: eventDates[0], dates: JSON.stringify(eventDates), startTime, endTime, allDay },
        });
        if (alive) setAvailability(data);
      } catch (err) {
        if (alive) setAvailability(null);
      } finally {
        if (alive) setCheckingAvailability(false);
      }
    }, 350);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [slug, eventDatesKey, eventDates, startTime, endTime, allDay]);

  const onPickCover = (e) => {
    const f = e.target.files?.[0];
    setCoverFile(f || null);
    setCoverPreview(f ? URL.createObjectURL(f) : '');
  };

  const removeCover = () => {
    setCoverFile(null);
    setCoverPreview('');
  };

  // --- Manejo de Ticket Types ---
  const addTicketType = () => {
    setTicketTypes([...ticketTypes, {
      id: Date.now(),
      name: '',
      price: '',
      totalQuantity: '',
      ticketsIncluded: 1,
      description: ''
    }]);
  };

  const removeTicketType = (id) => {
    if (ticketTypes.length === 1) return; // No permitir borrar el último
    setTicketTypes(ticketTypes.filter(t => t.id !== id));
  };

  const updateTicketType = (id, field, value) => {
    setTicketTypes(ticketTypes.map(t =>
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const addAdditionalDate = (dateToAdd = additionalDate) => {
    if (!dateToAdd || dateToAdd === date || additionalDates.includes(dateToAdd)) return;
    setAdditionalDates((current) => normalizeEventDates('', [...current, dateToAdd]));
    setAdditionalDate('');
  };

  const removeAdditionalDate = (dateToRemove) => {
    setAdditionalDates((current) => current.filter((item) => item !== dateToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMessage('');

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);

    formData.append('date', date);
    formData.append('dates', JSON.stringify(eventDates));
    formData.append('startTime', startTime);
    formData.append('endTime', endTime);
    formData.append('allDay', allDay);
    formData.append('address', address);
    formData.append('city', city);
    formData.append('province', province);
    formData.append('country', country);
    formData.append('location', `${address}, ${city}`);

    // Legacy fields calculation
    const normalizedTicketTypes = isBookingMode ? [] : normalizeTicketInventory(ticketTypes);
    const legacyPrice = isBookingMode ? 0 : Math.min(...normalizedTicketTypes.map(t => Number(t.price) || 0));
    const legacyTotal = isBookingMode ? 0 : normalizedTicketTypes.reduce((sum, t) => sum + (Number(t.totalQuantity) || 0), 0);

    formData.append('salesMode', salesMode);
    formData.append('price', legacyPrice);
    formData.append('totalTickets', legacyTotal);
    formData.append('expenses', normalizeNonNegativeInput(expenses));
    formData.append('bookingAmount', normalizeNonNegativeInput(bookingAmount));
    formData.append('bookingPaidAmount', normalizeNonNegativeInput(bookingPaidAmount));
    formData.append('bookingPaymentStatus', bookingPaymentStatus);
    formData.append('bookingNotes', bookingNotes);
    formData.append('isPublic', isBookingMode || isSubscriptionClient ? false : isPublic);
    formData.append('ticketTypes', JSON.stringify(normalizedTicketTypes));

    if (coverFile) {
      formData.append('cover', coverFile);
    }

    try {
      let newEvent;

      try {
        newEvent = await dispatch(createEvent({ slug, data: formData })).unwrap();
      } catch (error) {
        if (error?.code !== 'SCHEDULE_CONFLICT') throw error;

        const conflictText = (error.conflicts || [])
          .map((event) => `- ${event.dateOnly ? `${event.dateOnly} - ` : ''}${event.title} (${event.allDay ? 'dia completo' : `${event.startTime || '--'}-${event.endTime || '--'}`})`)
          .join('\n');
        const confirmed = window.confirm(
          `Esta fecha u horario ya esta ocupado:\n${conflictText}\n\nQueres crear el evento igual?`
        );

        if (!confirmed) throw error;
        formData.append('allowScheduleConflict', 'true');
        newEvent = await dispatch(createEvent({ slug, data: formData })).unwrap();
      }

      setSuccessMessage('Evento creado con éxito.');
      setCreatedEvent(newEvent);
      setCreatedBatchCount(eventDates.length);
      setShowShareModal(true);
      // No navegamos automáticamente, dejamos que el usuario elija en el modal
    } catch (error) {
      console.error('Error al crear evento:', error);
      alert(error.error || error.message || 'Error al crear el evento. Verificá los campos e intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  // --- Helpers para compartir ---
  const shareUrl = createdEvent
    ? `${process.env.REACT_APP_API_URL || window.location.origin}/share/${slug}/events/${createdEvent._id}`
    : '';

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const encodedShareUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(createdEvent?.title || '');
  const encodedText = encodeURIComponent(`¡No te pierdas ${createdEvent?.title}! \nConseguí tus entradas acá:`);

  const shareLinks = [
    {
      name: 'WhatsApp',
      href: `https://wa.me/?text=${encodedText}%20${encodedShareUrl}`,
      color: 'bg-[#25D366] hover:bg-[#1dbf57]',
      icon: (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      )
    },
    // Se pueden agregar más redes aquí si se desea
  ];

  return (
    <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30 pb-20 overflow-x-hidden">
      {/* Glow Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-orange/5 rounded-full blur-[120px] animate-glow" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-red/5 rounded-full blur-[120px] animate-glow-delayed" />
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-12 relative z-10">
        <button
          onClick={() => navigate(`/${slug}/admin/events`)}
          className="flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 group"
        >
          <ChevronLeft size={20} className="transition-transform group-hover:-translate-x-1" />
          <span className="font-black text-[10px] uppercase tracking-[2px]">Volver a eventos</span>
        </button>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
          <div>
            <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-2 uppercase">Crear <span className="text-gradient">Evento</span></h1>
            <p className="text-white/40 font-medium tracking-tight">Estás a un paso de lanzar tu próximo gran show.</p>
          </div>
          {successMessage && (
            <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-6 py-3 rounded-2xl font-bold animate-in fade-in zoom-in duration-300">
              {successMessage}
            </div>
          )}
        </div>

        {!loadingSalon && isMpRequired && (
          <div className="mb-12 bg-primary-orange/10 border border-primary-orange/20 p-8 rounded-[40px] flex flex-col md:flex-row items-center gap-6 animate-fade-in-up">
            <div className="w-16 h-16 bg-primary-orange/20 rounded-3xl flex items-center justify-center text-primary-orange shrink-0">
              <XCircle size={32} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-black uppercase text-primary-orange mb-1">Mercado Pago no conectado</h3>
              <p className="text-white/60 font-medium leading-relaxed">
                Para poder vender entradas y cobrar al instante, necesitás vincular tu cuenta de Mercado Pago.
                Sin esto, no podés publicar eventos.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/${slug}/admin/settings?focus=mp`)}
              className="px-8 py-4 bg-primary-orange text-black font-black rounded-2xl hover:scale-105 transition-all shadow-lg shadow-orange-500/20"
            >
              CONECTAR AHORA
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-glass rounded-[40px] p-8 md:p-10 shadow-3xl space-y-8">
              <InputGroup label="Título del Evento" icon={<Info size={12} className="text-primary-orange" />}>
                <input
                  type="text"
                  placeholder="Ej: Noche Inolvidable @ Passar"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-lg font-black placeholder:text-white/10 focus:border-primary-orange/50 transition-all focus:ring-4 focus:ring-primary-orange/5 outline-none"
                  required
                />
              </InputGroup>

              <InputGroup label="Descripción" icon={<Plus size={12} className="text-primary-orange" />}>
                <textarea
                  placeholder="Contanos de qué se trata el evento..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-medium placeholder:text-white/10 focus:border-primary-orange/50 transition-all focus:ring-4 focus:ring-primary-orange/5 outline-none h-48 resize-none"
                  required
                />
              </InputGroup>

              <InputGroup label="Modalidad" icon={<Ticket size={12} className="text-primary-orange" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSalesMode('tickets')}
                    className={`text-left rounded-2xl border p-5 transition-all ${salesMode === 'tickets' ? 'bg-primary-orange/10 border-primary-orange/40 text-white' : 'bg-white/5 border-white/10 text-white/55 hover:bg-white/10'}`}
                  >
                    <p className="text-sm font-black">Vender entradas online</p>
                    <p className="text-[10px] font-medium mt-2 leading-relaxed">Usa stock, tipos de ticket, Mercado Pago, QR y control de accesos.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSalesMode('booking');
                      setIsPublic(false);
                    }}
                    className={`text-left rounded-2xl border p-5 transition-all ${salesMode === 'booking' ? 'bg-blue-500/10 border-blue-500/40 text-white' : 'bg-white/5 border-white/10 text-white/55 hover:bg-white/10'}`}
                  >
                    <p className="text-sm font-black">Solo agenda y cobros</p>
                    <p className="text-[10px] font-medium mt-2 leading-relaxed">Reserva la fecha, registra seña y saldo sin publicar tickets.</p>
                  </button>
                </div>
              </InputGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup label="Fecha" icon={<Calendar size={12} className="text-primary-orange" />}>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-primary-orange/50 transition-all outline-none"
                    required
                  />
                </InputGroup>

                <InputGroup label="Sumar dias" icon={<Plus size={12} className="text-primary-orange" />}>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={additionalDate}
                      onChange={(e) => addAdditionalDate(e.target.value)}
                      className="min-w-0 flex-1 bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-primary-orange/50 transition-all outline-none"
                    />
                  </div>
                  {eventDates.length > 1 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {eventDates.map((eventDate) => (
                        <span key={eventDate} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-primary-orange/10 border border-primary-orange/20 text-primary-orange text-[10px] font-black">
                          {eventDate}
                          {eventDate !== date && (
                            <button type="button" onClick={() => removeAdditionalDate(eventDate)} className="text-white/40 hover:text-white">
                              <XCircle size={14} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </InputGroup>

                <div className="grid grid-cols-2 gap-4">
                  <InputGroup label="Apertura" icon={<Clock size={12} className="text-primary-orange" />}>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      disabled={allDay}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-primary-orange/50 transition-all outline-none disabled:opacity-40"
                    />
                  </InputGroup>
                  <InputGroup label="Cierre" icon={<Clock size={12} className="text-primary-orange" />}>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      disabled={allDay}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-primary-orange/50 transition-all outline-none disabled:opacity-40"
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center justify-between gap-4 bg-white/5 border border-white/10 rounded-2xl p-5 cursor-pointer">
                  <div>
                    <p className="text-sm font-black text-white">Reservar dia completo</p>
                    <p className="text-[10px] text-white/40 mt-1">Bloquea toda la fecha en la agenda del salon.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={allDay}
                    onChange={(e) => setAllDay(e.target.checked)}
                    className="h-5 w-5 accent-primary-orange"
                  />
                </label>

                {(checkingAvailability || availability) && (
                  <div className={`rounded-2xl border p-4 ${availability?.available ? 'bg-green-500/10 border-green-500/20 text-green-300' : 'bg-primary-orange/10 border-primary-orange/25 text-primary-orange'}`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-black">
                          {checkingAvailability
                            ? 'Consultando disponibilidad...'
                            : availability?.available
                              ? eventDates.length > 1 ? `${eventDates.length} fechas disponibles` : 'Fecha disponible'
                              : 'Fecha u horario ocupado'}
                        </p>
                        {!checkingAvailability && !availability?.available && availability?.conflicts?.length > 0 && (
                          <p className="text-xs font-medium mt-1 text-white/60">
                            {availability.conflicts.map((event) => event.title).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* TICKET TYPES SECTION */}
              {!isBookingMode && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Ticket size={16} className="text-primary-orange" />
                  <h3 className="text-sm font-black uppercase text-white/50 tracking-widest">Tipos de Tickets</h3>
                </div>

                <div className="space-y-3">
                  {ticketTypes.map((ticket, index) => (
                    <div key={ticket.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 relative group hover:border-white/20 transition-all">
                      {ticketTypes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTicketType(ticket.id)}
                          className="absolute top-4 right-4 p-2 text-white/20 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-white/30">Nombre del Ticket</label>
                          <input
                            type="text"
                            placeholder="Ej: General, VIP, Early Bird..."
                            value={ticket.name}
                            onChange={(e) => updateTicketType(ticket.id, 'name', e.target.value)}
                            className="w-full bg-black/20 border border-white/5 rounded-xl p-3 text-white font-bold text-sm outline-none focus:border-primary-orange/50"
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-white/30">Descripción (Opcional)</label>
                          <input
                            type="text"
                            placeholder="Ej: Incluye consumición..."
                            value={ticket.description}
                            onChange={(e) => updateTicketType(ticket.id, 'description', e.target.value)}
                            className="w-full bg-black/20 border border-white/5 rounded-xl p-3 text-white/70 text-sm outline-none focus:border-primary-orange/50"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-white/30">Precio ($)</label>
                          <input
                            type="number"
                            placeholder="0"
                            value={ticket.price}
                            onChange={(e) => updateTicketType(ticket.id, 'price', e.target.value)}
                            className="w-full bg-black/20 border border-white/5 rounded-xl p-3 text-white font-bold text-lg outline-none focus:border-primary-orange/50"
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-white/30">Stock</label>
                          <input
                            type="number"
                            placeholder="10"
                            value={ticket.totalQuantity}
                            onChange={(e) => updateTicketType(ticket.id, 'totalQuantity', e.target.value)}
                            className="w-full bg-black/20 border border-white/5 rounded-xl p-3 text-white font-bold text-lg outline-none focus:border-primary-orange/50"
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-white/30">Entradas por Ticket</label>
                          <input
                            type="number"
                            placeholder="1"
                            value={ticket.ticketsIncluded}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateTicketType(ticket.id, 'ticketsIncluded', val === '' ? '' : parseInt(val));
                            }}
                            className="w-full bg-black/20 border border-white/5 rounded-xl p-3 text-white font-bold text-lg outline-none focus:border-primary-orange/50"
                            min="1"
                          />
                          <p className="text-[9px] text-white/20">Ej: 2 para 2x1</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addTicketType}
                    className="w-full py-3 rounded-2xl border border-dashed border-white/20 text-white/40 hover:text-white hover:border-white/40 hover:bg-white/5 transition-all text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    <Plus size={16} /> Agregar Tipo de Ticket
                  </button>
                </div>
              </div>
              )}
            </div>

            {/* Address Section */}
            <div className="bg-glass rounded-[40px] p-8 md:p-10 shadow-3xl space-y-8">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 bg-brand-gradient rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-500/20">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black uppercase">Ubicación</h3>
                  <p className="text-[10px] font-black text-white/20 uppercase tracking-[2px]">¿Dónde es la fiesta?</p>
                </div>
              </div>

              {salonHasLocation && (
                <button
                  type="button"
                  onClick={applySalonLocation}
                  className="w-full flex items-center justify-between gap-4 rounded-2xl border border-primary-orange/20 bg-primary-orange/10 px-5 py-4 text-left text-primary-orange hover:bg-primary-orange/15 transition-all"
                >
                  <span>
                    <span className="block text-sm font-black text-white">Usar ubicacion del salon</span>
                    <span className="block text-[10px] font-medium text-white/50 mt-1">
                      {[salon.address, salon.city, salon.province].filter(Boolean).join(', ')}
                    </span>
                  </span>
                  <MapPin size={18} />
                </button>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup label="Dirección" icon={<Map size={12} className="text-primary-orange" />}>
                  <input
                    type="text"
                    placeholder="Calle y altura"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 transition-all"
                    required
                  />
                </InputGroup>
                <InputGroup label="Ciudad" icon={<Building2 size={12} className="text-primary-orange" />}>
                  <input
                    type="text"
                    placeholder="Ej: Buenos Aires"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 transition-all"
                    required
                  />
                </InputGroup>
                <InputGroup label="Provincia" icon={<MapPin size={12} className="text-primary-orange" />}>
                  <select
                    value={province}
                    onChange={(e) => setProvince(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 transition-all appearance-none"
                    required
                  >
                    <option value="" className="bg-[#080808]">Seleccionar...</option>
                    {[
                      'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos',
                      'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén', 'Río Negro',
                      'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
                    ].map(p => (
                      <option key={p} value={p} className="bg-[#080808]">{p}</option>
                    ))}
                  </select>
                </InputGroup>
                <InputGroup label="País" icon={<Globe size={12} className="text-primary-orange" />}>
                  <input
                    type="text"
                    disabled
                    value={country}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white/40 font-black outline-none"
                  />
                </InputGroup>
              </div>
            </div>
          </div>

          {/* Sidebar / Config */}
          <div className="lg:col-span-4 space-y-8">
            {/* Portada */}
            <div className="bg-glass rounded-[40px] p-8 shadow-3xl">
              <InputGroup label="Imagen de Portada" icon={<ImageIcon size={12} className="text-primary-orange" />}>
                <div className="relative group/cover aspect-video bg-white/5 border-2 border-dashed border-white/10 rounded-3xl overflow-hidden mt-2">
                  {coverPreview ? (
                    <>
                      <img src={coverPreview} alt="Preview" className="w-full h-full object-cover transition-transform duration-700 group-hover/cover:scale-110" />
                      <button
                        type="button"
                        onClick={removeCover}
                        className="absolute top-3 right-3 p-2 bg-black/60 backdrop-blur-md rounded-full text-red-400 hover:scale-110 transition-all border border-white/10"
                      >
                        <XCircle size={20} />
                      </button>
                    </>
                  ) : (
                    <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-white/[0.02] transition-colors">
                      <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-white/20 mb-3 font-black">+</div>
                      <span className="text-[10px] font-black uppercase tracking-[2px] text-white/20">Subir Imagen</span>
                      <input type="file" className="hidden" accept="image/*" onChange={onPickCover} />
                    </label>
                  )}
                </div>
              </InputGroup>
            </div>

            {/* Tickets & Expenses */}
            <div className="bg-glass rounded-[40px] p-8 shadow-3xl space-y-8">

              {isBookingMode && (
                <div className="space-y-5">
                  <div className="p-5 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
                    <h4 className="text-sm font-black text-blue-300">Reserva privada</h4>
                    <p className="text-[10px] text-white/50 mt-2 leading-relaxed">
                      Este evento solo bloquea agenda y registra cobros internos. No se publican entradas online.
                    </p>
                  </div>
                  <InputGroup label="Monto acordado" icon={<Calculator size={12} className="text-primary-orange" />}>
                    <input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={bookingAmount}
                      onChange={(e) => setBookingAmount(e.target.value)}
                      onBlur={(e) => setBookingAmount(normalizeNonNegativeInput(e.target.value))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 transition-all"
                    />
                  </InputGroup>
                  <InputGroup label="Seña / cobrado" icon={<DollarSign size={12} className="text-primary-orange" />}>
                    <input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={bookingPaidAmount}
                      onChange={(e) => setBookingPaidAmount(e.target.value)}
                      onBlur={(e) => setBookingPaidAmount(normalizeNonNegativeInput(e.target.value))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 transition-all"
                    />
                  </InputGroup>
                  <InputGroup label="Estado de cobro" icon={<Info size={12} className="text-primary-orange" />}>
                    <select
                      value={bookingPaymentStatus}
                      onChange={(e) => setBookingPaymentStatus(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 transition-all"
                    >
                      <option value="pending" className="bg-[#080808]">Pendiente</option>
                      <option value="partial" className="bg-[#080808]">Seña parcial</option>
                      <option value="paid" className="bg-[#080808]">Cobrado completo</option>
                    </select>
                  </InputGroup>
                  <InputGroup label="Notas internas" icon={<Plus size={12} className="text-primary-orange" />}>
                    <textarea
                      value={bookingNotes}
                      onChange={(e) => setBookingNotes(e.target.value)}
                      placeholder="Detalle del acuerdo, cuotas, responsable..."
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-medium outline-none focus:border-primary-orange/50 transition-all h-28 resize-none"
                    />
                  </InputGroup>
                </div>
              )}

              <InputGroup label="Gastos Estimados" icon={<Calculator size={12} className="text-primary-orange" />}>
                <input
                  type="number"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={expenses}
                  onChange={(e) => setExpenses(e.target.value)}
                  onBlur={(e) => setExpenses(normalizeNonNegativeInput(e.target.value))}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 transition-all"
                />
                <p className="text-[9px] font-medium text-white/20 mt-1 pl-1">Este valor ayuda a calcular tu utilidad neta.</p>
              </InputGroup>

              {/* Toggle Public / Private */}
              {isBookingMode ? (
                <div className="p-5 bg-blue-500/5 border border-blue-500/20 rounded-2xl">
                  <h4 className="text-sm font-black text-blue-300">No publicado</h4>
                  <p className="text-[10px] text-white/50 mt-2 leading-relaxed">
                    Las reservas solo aparecen en tu agenda y en la administracion del salon.
                  </p>
                </div>
              ) : isSubscriptionClient ? (
                <div className="p-5 bg-primary-orange/5 border border-primary-orange/20 rounded-2xl">
                  <h4 className="text-sm font-black text-primary-orange">Evento por invitación</h4>
                  <p className="text-[10px] text-white/50 mt-2 leading-relaxed">
                    En salones suscriptos no se habilita la venta pública. El evento queda privado y las entradas se emiten una por una desde la gestión del salón.
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between p-5 bg-white/5 border border-white/10 rounded-2xl">
                  <div>
                    <h4 className="text-sm font-black text-white">Evento Público</h4>
                    <p className="text-[10px] text-white/40 mt-1">Si está activo, el evento aparecerá en la página principal (Home). Si se desactiva, será privado y solo visible usando el enlace de tu salón.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-orange"></div>
                  </label>
                </div>
              )}
            </div>

            {/* Disclaimer Comisiones */}
            {isBookingMode && (
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-3xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0 text-blue-300">
                    <Calculator size={20} />
                  </div>
                  <div>
                    <h4 className="text-blue-300 font-black text-sm uppercase tracking-widest mb-1">
                      Reserva interna
                    </h4>
                    <p className="text-white/60 text-xs font-medium leading-relaxed">
                      Este evento queda fuera del circuito de venta online. Te sirve para bloquear la fecha, anotar el monto acordado y controlar seña o saldo pendiente.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!isBookingMode && (
            <div className="bg-primary-orange/5 border border-primary-orange/20 rounded-3xl p-6">
               <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-orange/20 flex items-center justify-center shrink-0 text-primary-orange">
                      <Calculator size={20} />
                  </div>
                  <div>
                      <h4 className="text-primary-orange font-black text-sm uppercase tracking-widest mb-1">
                        {isSubscriptionClient ? 'Emisión manual' : 'Recibís el 100%'}
                      </h4>
                      <p className="text-white/60 text-xs font-medium leading-relaxed">
                          {isSubscriptionClient
                            ? 'Este evento no se vende de forma pública. Desde la gestión del salón vas a poder emitir invitaciones individuales y controlar quién recibe cada acceso.'
                            : 'La publicación de este evento no tiene costo para vos. El comprador abona un 14% adicional: 4% corresponde a PASSAR y el 10% restante queda acreditado a tu operación para cubrir costos de cobro. Así recibís el valor publicado de cada entrada.'}
                      </p>
                  </div>
               </div>
            </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={saving || (!loadingSalon && isMpRequired)}
              className="group w-full bg-brand-gradient py-6 rounded-[32px] font-black text-white text-lg shadow-xl shadow-orange-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-3 shine-effect"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={24} />
                  GUARDANDO...
                </>
              ) : isMpRequired && !loadingSalon ? (
                <>
                  <XCircle size={24} />
                  MP REQUERIDO
                </>
              ) : (
                <>
                  <Send size={24} />
                  {isBookingMode ? 'GUARDAR RESERVA' : 'PUBLICAR EVENTO'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* --- MODAL DE ÉXITO A PANTALLA COMPLETA --- */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-6">
          <div className="bg-glass border border-white/10 rounded-[32px] max-w-md w-full p-8 relative animate-in zoom-in-90 fade-in duration-300">
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                <Check size={40} className="text-green-500" />
              </div>

              <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-wide">¡Evento Creado!</h2>
              {isBookingMode ? (
              <p className="text-white/60 mb-8 font-medium">
                {createdBatchCount > 1
                  ? `La reserva quedo guardada en ${createdBatchCount} dias de agenda con el estado de cobro que cargaste.`
                  : 'La reserva quedo guardada en la agenda con el estado de cobro que cargaste.'}
              </p>
              ) : (
              <p className="text-white/60 mb-8 font-medium">
                {createdBatchCount > 1
                  ? `El evento quedo programado para ${createdBatchCount} dias.`
                  : isSubscriptionClient
                  ? 'Tu evento quedó privado para que puedas emitir invitaciones individuales desde la gestión del salón.'
                  : 'Tu evento ya está público. ¡Compartilo ahora para empezar a vender!'}
              </p>
              )}

              {/* Botón WhatsApp Gigante */}
              {!isBookingMode && !isSubscriptionClient && shareLinks.map(link => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl text-white font-bold mb-4 ${link.color} transition-transform hover:scale-[1.02] shadow-lg`}
                >
                  {link.icon}
                  Compartir en {link.name}
                </a>
              ))}

              {/* Copiar Link */}
              {!isBookingMode && !isSubscriptionClient && (
                <button
                  onClick={handleCopyLink}
                  className="w-full bg-white/5 border border-white/10 hover:bg-white/10 py-4 rounded-xl text-white/80 font-bold mb-8 flex items-center justify-center gap-2 transition-all"
                >
                  {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
                  {copied ? '¡Enlace Copiado!' : 'Copiar enlace al evento'}
                </button>
              )}

              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={() => navigate(`/${slug}/admin/events`)}
                  className="bg-brand-gradient py-3.5 rounded-xl font-bold text-white text-sm uppercase tracking-wider hover:opacity-90"
                >
                  Ir a Mis Eventos
                </button>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setCreatedEvent(null);
                    setCreatedBatchCount(1);
                    const defaults = getSalonLocationDefaults(salon);
                    // Limpiar form
                    setTitle(''); setDescription(''); setDate(''); setAdditionalDate(''); setAdditionalDates([]); setStartTime(''); setEndTime(''); setAllDay(false); setSalesMode('tickets'); setBookingAmount(''); setBookingPaidAmount(''); setBookingPaymentStatus('pending'); setBookingNotes(''); setAddress(defaults.address); setCity(defaults.city); setProvince(defaults.province); setCountry(defaults.country);
                    setCoverFile(null); setCoverPreview(''); setExpenses('');

                    setTicketTypes([{ id: Date.now(), name: '', price: '', totalQuantity: MIN_EVENT_TICKETS, ticketsIncluded: 1, description: '' }]);
                  }}
                  className="py-3.5 rounded-xl font-bold text-white/40 text-sm uppercase tracking-wider hover:text-white"
                >
                  Crear otro evento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreateEvent;
